import { SEELAY_BUILT_BY_ANSWER } from "./seelayBrand";

export const SITE_URL = "https://www.pagetest.in";
export const OG_IMAGE = `${SITE_URL}/og.png`;

export const HOMEPAGE_META = {
  title: "PageTest | Measure, Monitor & Improve Web Performance",
  description:
    "Free web performance platform: Google Lighthouse audits, Core Web Vitals, test history, scheduled checks, Google Drive export, and context-aware AI fix suggestions for desktop and mobile.",
  keywords:
    "website speed test, Core Web Vitals, Google Lighthouse, PageSpeed Insights, web performance monitoring, AI performance fixes, LCP, CLS, TBT, free speed test, Google Drive export",
};

export const HOMEPAGE_FAQS = [
  {
    question: "Is PageTest free to use?",
    answer:
      "Yes. You can run Lighthouse audits, view issue breakdowns, enable AI suggestions on a test, and share report links without paying or signing in.",
  },
  {
    question: "How is PageTest different from Google PageSpeed Insights?",
    answer:
      "Both use Lighthouse. PageTest adds a persistent workflow: separate desktop and mobile runs in one session, a dashboard with test history, scheduled weekly checks, Google Drive export, shareable reports, and AI fix steps grounded in your audit data, detected stack, and affected file paths.",
  },
  {
    question: "What Core Web Vitals does PageTest measure?",
    answer:
      "Each audit reports Lighthouse performance scores plus metrics including Largest Contentful Paint (LCP), Cumulative Layout Shift (CLS), Total Blocking Time (TBT), First Contentful Paint (FCP), and Speed Index, for both mobile and desktop strategies.",
  },
  {
    question: "How do AI suggestions work?",
    answer:
      "When enabled on a test, PageTest sends Lighthouse issues, Core Web Vitals, detected framework hints (e.g. Next.js, WordPress), and real resource paths to an AI model. Suggestions mirror the Issues tab and include stack-specific fix steps, not generic optimization boilerplate.",
  },
  {
    question: "Can I track website performance over time?",
    answer:
      "Signed-in users get test history in the dashboard. You can map URLs for recurring Drive exports and enable weekly scheduled checks so new runs are recorded automatically.",
  },
  {
    question: "Who builds PageTest?",
    answer: SEELAY_BUILT_BY_ANSWER,
  },
];

export function buildFaqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer,
      },
    })),
  };
}

export function buildHomepageSchema() {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: "PageTest",
        url: SITE_URL,
        description: HOMEPAGE_META.description,
        publisher: { "@id": `${SITE_URL}/#organization` },
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${SITE_URL}/test?url={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": `${SITE_URL}/#webpage`,
        url: `${SITE_URL}/`,
        name: HOMEPAGE_META.title,
        description: HOMEPAGE_META.description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#software` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: OG_IMAGE,
        },
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${SITE_URL}/#software`,
        name: "PageTest",
        url: SITE_URL,
        applicationCategory: "DeveloperApplication",
        operatingSystem: "Web",
        description: HOMEPAGE_META.description,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
        featureList: [
          "Google Lighthouse audits (performance, accessibility, SEO, best practices)",
          "Core Web Vitals: LCP, CLS, TBT, FCP, Speed Index",
          "Desktop and mobile testing in one session",
          "Context-aware AI fix suggestions",
          "Test history and dashboard",
          "Weekly scheduled checks",
          "Google Drive export",
          "Shareable report links",
        ],
        screenshot: {
          "@type": "ImageObject",
          url: OG_IMAGE,
        },
        creator: { "@id": `${SITE_URL}/#organization` },
      },
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Seelay",
        url: "https://www.seelay.in",
        logo: "https://www.seelay.in/icons/icon-96x96.png",
        sameAs: ["https://www.linkedin.com/company/seelay"],
      },
    ],
  };
}
